import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Modal from "../../general/Modal";
import Loader from "../../general/Loader";
import { postRequest } from "../../../utils/request";
import { arrayToCsv, downloadBlob, getLink, toast } from "../../../utils/helpers";
import { useHistory } from "../../../utils/router";

const columnasVacias = { referencia: '', cantidad: '', almacen: '', unidad: '' };

// Texto que busca cada columna en los encabezados para preseleccionarla.
const claves = { referencia: 'ref', cantidad: 'cant', almacen: 'almac', unidad: 'unid' };

const etiquetas = { referencia: 'Referencia', cantidad: 'Cantidad', almacen: 'Almacén', unidad: 'Unidad' };

const normalizar = (texto) => String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const preseleccionar = (encabezados) => Object.fromEntries(
    Object.entries(claves).map(([campo, clave]) => {
        const indice = encabezados.findIndex((encabezado) => normalizar(encabezado).includes(clave));
        return [campo, indice >= 0 ? indice : ''];
    })
);

const pasos = ['Archivo', 'Columnas', 'Resultado'];

// Errores que se corrigen en el paso del archivo; el resto (columnas.*) en el del mapeo.
const camposDelArchivo = ['fecha', 'titulo', 'archivo'];

const ImportarCsv = ({ show, toggle, onExportar, onImportado }) => {
    const history = useHistory();
    const [paso, setPaso] = useState(1);
    const [fecha, setFecha] = useState(dayjs().format('YYYY-MM-DD'));
    const [titulo, setTitulo] = useState('');
    // Mientras el usuario no lo edite, el título sigue al nombre del archivo.
    const [tituloEditado, setTituloEditado] = useState(false);
    const [archivo, setArchivo] = useState(null);
    const [encabezados, setEncabezados] = useState([]);
    const [previa, setPrevia] = useState([]);
    const [totalFilas, setTotalFilas] = useState(0);
    const [columnas, setColumnas] = useState(columnasVacias);
    const [errors, setErrors] = useState({});
    const [resultado, setResultado] = useState(null);
    const [cargando, setCargando] = useState('');
    // Cancelar no aborta la petición en curso: su respuesta se descarta si el modal ya se cerró o se eligió otro archivo.
    const solicitud = useRef(0);

    const reiniciarMapeo = () => {
        setPaso(1);
        setEncabezados([]);
        setPrevia([]);
        setTotalFilas(0);
        setColumnas(columnasVacias);
        setResultado(null);
    };

    useEffect(() => {
        solicitud.current++;
        if (!show) return;

        reiniciarMapeo();
        setFecha(dayjs().format('YYYY-MM-DD'));
        setTitulo('');
        setTituloEditado(false);
        setArchivo(null);
        setErrors({});
        setCargando('');
    }, [show]);

    const elegirArchivo = async (e) => {
        const seleccionado = e.target.files?.[0] ?? null;
        e.target.value = ''; // para que elegir de nuevo el mismo archivo vuelva a dispararse
        if (!seleccionado) return;

        reiniciarMapeo();
        setErrors({});
        setArchivo(seleccionado);

        if (!tituloEditado || !titulo.trim()) setTitulo(seleccionado.name.replace(/\.[^.]+$/, ''));

        const fd = new FormData();
        fd.append('archivo', seleccionado);

        const actual = ++solicitud.current;
        setCargando('Leyendo el archivo...');
        const response = await postRequest('movimientos/importar/previa', fd);
        if (actual !== solicitud.current) return;
        setCargando('');

        if (response?.success) {
            setEncabezados(response.data.encabezados);
            setPrevia(response.data.previa);
            setTotalFilas(response.data.total_filas);
            setColumnas(preseleccionar(response.data.encabezados));
        } else {
            setErrors({ archivo: response?.data?.message?.archivo ?? ['No se pudo leer el archivo.'] });
        }
    };

    const cambiarColumna = (campo, valor) => setColumnas({ ...columnas, [campo]: valor === '' ? '' : +valor });

    const puedeAvanzar = !!archivo && encabezados.length > 0;

    const puedeImportar = columnas.referencia !== '' && columnas.cantidad !== '' && columnas.referencia !== columnas.cantidad;

    const verMovimiento = (id) => history.push(getLink('/productos/inventario-masivo/' + id));

    const importar = async () => {
        if (!puedeImportar || cargando) return;

        const fd = new FormData();
        fd.append('fecha', fecha);
        fd.append('titulo', titulo);
        fd.append('archivo', archivo);
        Object.entries(columnas).forEach(([campo, indice]) => {
            if (indice !== '') fd.append(`columnas[${campo}]`, indice);
        });

        setErrors({});
        setResultado(null);
        const actual = ++solicitud.current;
        setCargando(`Importando ${totalFilas} filas...`);
        const response = await postRequest('movimientos/importar', fd);
        if (actual !== solicitud.current) return;
        setCargando('');

        const data = response?.data ?? {};

        if (response?.success) {
            onImportado?.();
            toast(`${data.movimiento.productos.length} productos ajustados, ${data.sin_cambios} sin cambios` + (data.sin_contar ? `, ${data.sin_contar} sin contar` : ''));

            setResultado({
                movimientoId: data.movimiento.id,
                codigo: data.movimiento.codigo,
                ajustados: data.movimiento.productos.length,
                sinCambios: data.sin_cambios,
                sinContar: data.sin_contar,
                encabezados: data.encabezados ?? encabezados,
                omitidos: data.omitidos,
            });
            setPaso(3);
            return;
        }

        if (data.omitidos !== undefined || data.sin_cambios !== undefined) {
            setResultado({
                mensaje: data.message,
                ajustados: 0,
                sinCambios: data.sin_cambios ?? 0,
                sinContar: data.sin_contar ?? 0,
                encabezados: data.encabezados ?? encabezados,
                omitidos: data.omitidos ?? [],
            });
            setPaso(3);
            return;
        }

        if (data.message && typeof data.message === 'object') {
            setErrors(data.message);
            if (Object.keys(data.message).some((campo) => camposDelArchivo.includes(campo))) setPaso(1);
        } else {
            toast(data.message ?? 'Error importando el archivo.', 'error');
        }
    };

    // El servidor devuelve cada fila omitida tal como venía, así sirve igual para CSV y Excel.
    const descargarOmitidos = () => {
        const encabezado = resultado.encabezados;
        const rellenar = (fila) => [...fila, ...Array(Math.max(encabezado.length - fila.length, 0)).fill('')];

        const omitidas = [...resultado.omitidos]
            .sort((a, b) => a.linea - b.linea)
            .map((omitido) => [...rellenar(omitido.valores ?? []), omitido.motivo]);

        // BOM para que Excel muestre los acentos, igual que la exportación.
        downloadBlob('﻿' + arrayToCsv([[...encabezado, 'motivo'], ...omitidas]), 'filas-omitidas.csv');
    };

    const campoDeColumna = (indice) => Object.keys(columnas).find((campo) => columnas[campo] === indice);

    const error = (campo) => errors[campo] && <small className="help-blockParams form-Text">{errors[campo][0]}</small>;

    const selectColumna = (campo, vacia, requerida = false) => <div className="col-12 col-md-3 form-group">
        <label className={requerida ? 'required' : ''} htmlFor={`columna-${campo}`}>
            Columna {etiquetas[campo].toLowerCase()}
            {!requerida && <small className="text-muted"> (opcional)</small>}
        </label>
        <select id={`columna-${campo}`} className="form-control" value={columnas[campo]} onChange={(e) => cambiarColumna(campo, e.target.value)}>
            <option value="">{vacia}</option>
            {encabezados.map((encabezado, i) => <option key={i} value={i}>{encabezado || `Columna ${i + 1}`}</option>)}
        </select>
        {error(`columnas.${campo}`)}
    </div>;

    const indicador = <div className="d-flex align-items-center mb-4">
        {pasos.map((nombre, i) => <React.Fragment key={nombre}>
            {i > 0 && <div className="flex-grow-1 mx-2" style={{ borderTop: '2px solid ' + (paso > i ? '#007bff' : '#dee2e6') }} />}
            <div className={`d-flex align-items-center ${paso === i + 1 ? 'font-weight-bold' : 'text-muted'}`}>
                <span
                    className={`badge badge-pill mr-2 ${paso >= i + 1 ? 'badge-primary' : 'badge-light'}`}
                    style={{ width: '26px', height: '26px', lineHeight: '20px', fontSize: '0.85rem' }}>
                    {paso > i + 1 ? <i className="zmdi zmdi-check" /> : i + 1}
                </span>
                {nombre}
            </div>
        </React.Fragment>)}
    </div>;

    const pasoArchivo = <>
        <div className="row">
            <div className="col-12 form-group">
                <label className="required" htmlFor="importar-archivo">Archivo (CSV o Excel)</label>
                <input type="file" className="d-none" id="importar-archivo" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={elegirArchivo} />
                {archivo
                    ? <div className={`d-flex align-items-center border rounded p-3 ${errors.archivo ? 'border-danger' : ''}`}>
                        <i className="zmdi zmdi-file-text mr-3" style={{ fontSize: '2rem', color: '#6c757d' }} />
                        <div className="mr-auto" style={{ minWidth: 0 }}>
                            <strong className="d-block text-truncate">{archivo.name}</strong>
                            {puedeAvanzar && <small className="text-muted">{totalFilas} filas</small>}
                        </div>
                        <label htmlFor="importar-archivo" className="btn btn-outline-secondary btn-sm mb-0 ml-3">
                            <i className="zmdi zmdi-refresh mr-1" /> Reemplazar
                        </label>
                    </div>
                    : <label htmlFor="importar-archivo" className="btn btn-outline-primary btn-block py-4 mb-0" style={{ borderStyle: 'dashed' }}>
                        <i className="zmdi zmdi-upload mr-2" /> Seleccionar archivo CSV o Excel
                    </label>}
                {error('archivo')}
            </div>
            <div className="col-12 col-md-4 form-group">
                <label className="required" htmlFor="importar-fecha">Fecha</label>
                <input type="date" className="form-control" id="importar-fecha" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                {error('fecha')}
            </div>
            <div className="col-12 col-md-8 form-group">
                <label className="required" htmlFor="importar-titulo">Título del inventario</label>
                <input
                    type="text"
                    className="form-control"
                    id="importar-titulo"
                    value={titulo}
                    onChange={(e) => {
                        setTitulo(e.target.value);
                        setTituloEditado(true);
                    }}
                    placeholder="Título"
                />
                {error('titulo')}
            </div>
        </div>

        <small className="d-block">
            Use el archivo de <a href="#" onClick={(e) => { e.preventDefault(); onExportar(); }}>Exportar inventario</a>, complete la columna cantidad con lo contado e impórtelo aquí.
        </small>
    </>;

    const pasoColumnas = <>
        <p className="mb-3">
            <strong>{archivo?.name}</strong> · {totalFilas} filas. Indique qué columna del archivo corresponde a cada dato.
        </p>

        <div className="row">
            {selectColumna('referencia', 'Seleccionar...', true)}
            {selectColumna('cantidad', 'Seleccionar...', true)}
            {selectColumna('almacen', '— Ninguna —')}
            {selectColumna('unidad', '— Ninguna (unidad base) —')}
        </div>

        <div className="table-responsive">
            <table className="table table-sm table-bordered mb-1">
                <thead>
                    <tr>
                        {encabezados.map((encabezado, i) => {
                            const campo = campoDeColumna(i);
                            return <th key={i} className={campo ? 'table-info' : ''}>
                                {encabezado}
                                {campo && <small className="d-block font-weight-bold">{etiquetas[campo]}</small>}
                            </th>;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {previa.map((fila, f) => <tr key={f}>
                        {encabezados.map((_, i) => <td key={i} className={campoDeColumna(i) ? 'table-info' : ''}>{fila[i] ?? ''}</td>)}
                    </tr>)}
                </tbody>
            </table>
        </div>
        <small className="text-muted">{totalFilas} filas</small>
    </>;

    const pasoResultado = resultado && <>
        {resultado.mensaje && <div className="alert alert-warning">{resultado.mensaje}</div>}
        {resultado.movimientoId && <div className={`alert ${resultado.omitidos.length ? 'alert-info' : 'alert-success'}`}>
            Se creó el movimiento <strong>{resultado.codigo}</strong> con los productos que difieren del inventario.
            {resultado.omitidos.length > 0 && ' Revise las filas omitidas.'}
        </div>}

        <div className="row text-center mb-3">
            <div className="col"><h3 className="mb-0">{resultado.ajustados}</h3><small className="text-muted">Productos ajustados</small></div>
            <div className="col"><h3 className="mb-0">{resultado.sinCambios}</h3><small className="text-muted">Sin cambios</small></div>
            <div className="col"><h3 className="mb-0">{resultado.sinContar}</h3><small className="text-muted">Sin contar</small></div>
            <div className="col"><h3 className={`mb-0${resultado.omitidos.length ? ' text-danger' : ''}`}>{resultado.omitidos.length}</h3><small className="text-muted">Omitidos</small></div>
        </div>

        {resultado.omitidos.length > 0 && <>
            <div className="table-responsive" style={{ maxHeight: '250px' }}>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Fila</th>
                            <th>Referencia</th>
                            <th>Motivo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resultado.omitidos.map((omitido) => <tr key={omitido.linea}>
                            <td>{omitido.linea}</td>
                            <td>{omitido.referencia}</td>
                            <td>{omitido.motivo}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </>}
    </>;

    // Fragmento vacío y no null: con footer falsy el Modal pone sus botones por defecto.
    // La importación crea el movimiento aunque se cierre el modal, así que mientras corre no se puede cancelar.
    const importando = cargando && paso === 2;

    const footer = <>
        <button type="button" className="btn btn-plain" onClick={toggle} disabled={importando}>Cancelar</button>
        {!cargando && paso === 1 && <>
            <button type="button" className="btn btn-primary" onClick={() => setPaso(2)} disabled={!puedeAvanzar}>Siguiente</button>
        </>}
        {!cargando && paso === 2 && <>
            <button type="button" className="btn btn-plain" onClick={() => setPaso(1)}>Atrás</button>
            <button type="button" className="btn btn-primary" onClick={importar} disabled={!puedeImportar}>Importar</button>
        </>}
        {!cargando && paso === 3 && <>
            {resultado?.omitidos.length > 0 && <button type="button" className="btn btn-outline-secondary mr-2" onClick={descargarOmitidos}>
                <i className="zmdi zmdi-download mr-2" />
                Descargar filas omitidas
            </button>}
            {!resultado?.movimientoId && <button type="button" className="btn btn-plain" onClick={() => setPaso(2)}>Volver a columnas</button>}
            {resultado?.movimientoId && <button type="button" className="btn btn-primary" onClick={() => verMovimiento(resultado.movimientoId)}>Ver movimiento</button>}
        </>}
    </>;

    return <Modal show={show} toggle={toggle} title="Importar Desde Archivo" size="lg" footer={footer}>
        {indicador}

        {cargando
            ? <div className="text-center py-4">
                <Loader />
                <p className="text-muted mb-0">{cargando}</p>
            </div>
            : <>
                {paso === 1 && pasoArchivo}
                {paso === 2 && pasoColumnas}
                {paso === 3 && pasoResultado}
            </>}
    </Modal>;
};

export default ImportarCsv;
